import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FaBoxes,
  FaCrown,
  FaChartLine,
  FaSync,
  FaUser,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import { fetchAllProducts, fetchBestsellers, getCurrentUser } from '../../services/apiService';
import { useLanguage } from '../../contexts/LanguageContext';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalBestsellers: 0,
    totalRevenue: 0
  });
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const userData = await getCurrentUser();
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
    fetchDashboardData();
  };

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch data from services (only what clients need)
      const [productsResponse, bestsellersResponse] = await Promise.allSettled([
        fetchAllProducts(1),
        fetchBestsellers()
      ]);

      const newStats = {
        totalProducts: productsResponse.status === 'fulfilled' ? (productsResponse.value.data?.totalProducts || productsResponse.value.data?.total || productsResponse.value.data?.count || 0) : 0,
        totalBestsellers: bestsellersResponse.status === 'fulfilled' ? (bestsellersResponse.value.data?.total || 0) : 0,
        totalRevenue: 0
      };

      // Calculate revenue (placeholder - would need actual order data)
      newStats.totalRevenue = newStats.totalBestsellers * 50; // Placeholder calculation

      setStats(newStats);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(t('clientDashboard.errors.loadDashboardData'));
    } finally {
      setIsLoading(false);
    }
  };

  const statsCards = [
    {
      title: t('clientDashboard.stats.bestsellers.title'),
      value: stats.totalBestsellers,
      icon: FaCrown,
      color: 'yellow',
      change: '+8%',
      changeType: 'increase',
      description: t('clientDashboard.stats.bestsellers.description')
    },
    {
      title: t('clientDashboard.stats.totalProducts.title'),
      value: stats.totalProducts,
      icon: FaBoxes,
      color: 'purple',
      change: '+5%',
      changeType: 'increase',
      description: t('clientDashboard.stats.totalProducts.description')
    },
    {
      title: t('clientDashboard.stats.revenue.title'),
      value: `${stats.totalRevenue.toLocaleString()} CHF`,
      icon: FaChartLine,
      color: 'green',
      change: '+15%',
      changeType: 'increase',
      description: t('clientDashboard.stats.revenue.description')
    }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
          >
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('clientDashboard.title')}</h1>
              <p className="text-sm text-gray-500 mt-1">{t('clientDashboard.subtitle')}</p>
            </div>
            <div className="mt-4 lg:mt-0">
              <button
                onClick={fetchDashboardData}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2.5 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaSync className={`text-sm ${isLoading ? 'animate-spin' : ''}`} />
                {t('clientDashboard.refresh')}
              </button>
            </div>
          </motion.div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Welcome Client Section */}
        <motion.div
          className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg p-8 mb-8 text-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <FaUser className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{t('clientDashboard.welcome.greeting', { name: currentUser?.name || 'Client' })}</h2>
              <p className="text-indigo-100">{t('clientDashboard.welcome.subtitle')}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-semibold mb-2">{t('clientDashboard.quickAccess.title')}</h3>
              <p className="text-sm text-indigo-100">{t('clientDashboard.quickAccess.description')}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-semibold mb-2">{t('clientDashboard.orders.title')}</h3>
              <p className="text-sm text-indigo-100">{t('clientDashboard.orders.description')}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-semibold mb-2">{t('clientDashboard.support.title')}</h3>
              <p className="text-sm text-indigo-100">{t('clientDashboard.support.description')}</p>
            </div>
          </div>
        </motion.div>

        {/* Statistics Cards - Client specific */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {statsCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${
                    card.color === 'blue' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                    card.color === 'purple' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                    card.color === 'yellow' ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                    card.color === 'green' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                    'bg-gradient-to-r from-gray-500 to-gray-600'
                  }`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex items-center gap-1">
                    {card.changeType === 'increase' ? (
                      <FaArrowUp className="text-green-500 text-sm" />
                    ) : (
                      <FaArrowDown className="text-red-500 text-sm" />
                    )}
                    <span className={`text-xs font-medium ${card.changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
                      {card.change}
                    </span>
                  </div>
                </div>
                <div className="mb-2">
                  {isLoading ? (
                    <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
                  ) : (
                    <h3 className="text-2xl font-bold text-gray-900">{card.value}</h3>
                  )}
                </div>
                <p className="text-sm font-medium text-gray-900">{card.title}</p>
                <p className="text-xs text-gray-500 mt-1">{card.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Client-specific content sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Overview */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('clientDashboard.productOverview.title')}</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FaBoxes className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">{t('clientDashboard.productOverview.totalProducts')}</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {isLoading ? (
                    <div className="animate-pulse bg-gray-200 h-6 w-12 rounded"></div>
                  ) : (
                    stats.totalProducts
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FaCrown className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-700">{t('clientDashboard.productOverview.bestsellers')}</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {isLoading ? (
                    <div className="animate-pulse bg-gray-200 h-6 w-12 rounded"></div>
                  ) : (
                    stats.totalBestsellers
                  )}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Quick Actions for Clients */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('clientDashboard.quickActions.title')}</h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/products')}
                className="w-full p-4 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors duration-200 text-left"
              >
                <div className="flex items-center gap-3">
                  <FaBoxes className="h-5 w-5" />
                  <div>
                    <p className="text-sm font-medium">{t('clientDashboard.quickActions.browseProducts.title')}</p>
                    <p className="text-xs opacity-75">{t('clientDashboard.quickActions.browseProducts.description')}</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => navigate('/bestsellers')}
                className="w-full p-4 rounded-lg bg-yellow-50 text-yellow-600 hover:bg-yellow-100 transition-colors duration-200 text-left"
              >
                <div className="flex items-center gap-3">
                  <FaCrown className="h-5 w-5" />
                  <div>
                    <p className="text-sm font-medium">{t('clientDashboard.quickActions.viewBestsellers.title')}</p>
                    <p className="text-xs opacity-75">{t('clientDashboard.quickActions.viewBestsellers.description')}</p>
                  </div>
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;



